import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, FormLabel, TextField, Tooltip, Button as ButtonMUI } from '@material-ui/core';
import { Add } from '@material-ui/icons';

import Button from '~/components/Button';
import { formatPrice, maskAndFormatPrice } from '~/util/format';
import {
  loadContractors,
  loadServices,
  loadCategoriesByServices,
  createContractorPricingV1Api,
  listPricingTemplatesApi,
  getPricingTemplateByIdApi,
  createPricingTemplateApi,
} from '~/services/api';
import { Form, Subtitle, InputComponent } from './styles';
import Snackbar, { SeverityType } from '~/components/Snackbar';
import { handleApiErrorResponse } from '~/services/handleErrors';
import {
  ContractorsResponse,
  PricingTemplate,
  ServiceCategory,
  ServicesResponse,
  PricingAssociationType,
  PriceAssociationTableType,
  PriceAssociationTableEnum,
} from '~/types';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import { Title } from '~/components/Title';
import Modal from '~/components/Modal';
import Loading from '~/components/Loading';

type TableIdType = { id: string; type: PriceAssociationTableType; label?: string };
type TableTypeOption = [PriceAssociationTableType, string];

type EquipmentItem = { name: string; value: number };

type TablePrice = {
  type?: PriceAssociationTableType;
  name?: string;
  kmlFile?: string;
  prices?: {
    code: PricingAssociationType;
    value: number;
  }[];
  equipment?: EquipmentItem;
};

const TABLE_TYPE_OPTIONS = Object.entries(PriceAssociationTableEnum) as TableTypeOption[];

const getTemplateLabel = (template?: Partial<PricingTemplate>) => {
  if (!template) return 'Tabela';
  return template.nome || template.tipo_tabela || 'Tabela';
};

const getTemplateTypeOption = (templateType?: string | null): TableTypeOption | null => {
  if (!templateType) return null;

  const matched = TABLE_TYPE_OPTIONS.find(([, label]) => label === templateType);
  return matched ?? null;
};

const mapTemplateToTablePrice = (template: Partial<PricingTemplate>): TablePrice => ({
  name: template.nome,
  prices: [
    { code: 'OUTPUT_LIMIT', value: Number(template.saida_ate_40km || 0) },
    { code: 'OVER_DISTANCE', value: Number(template.km_excedente || 0) },
    { code: 'STOP_TIME', value: Number(template.hp || 0) },
    { code: 'HT', value: Number(template.ht || 0) },
    { code: 'DAILY', value: Number(template.diaria_na_base || 0) },
    { code: 'CANCELLATION_FEE', value: Number(template.taxa_cancelamento_saida || 0) },
    { code: 'DEFAULT_CANCELLATION_FEE', value: Number(template.taxa_cancelamento_padrao || 0) },
    { code: 'SPECIAL_EXIT', value: Number(template.saida_noite_sabdom || 0) },
  ],
  equipment: {
    name: template.equipamento || '',
    value: Number(template.valor_equipamento || 0),
  },
});

const getPriceValue = (table: TablePrice | undefined, code: PricingAssociationType) =>
  table?.prices?.find(item => item.code === code)?.value || 0;

export interface TablePricesContractorProps {
  embedded?: boolean;
  initialContractor?: ContractorsResponse | null;
}

export default function TablePricesContractor({
  embedded = false,
  initialContractor = null,
}: TablePricesContractorProps) {
  const [tablePrices, setTablePrices] = useState<TablePrice>();
  const [newTablePrices, setNewTablePrices] = useState<TablePrice>();
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState<ContractorsResponse[]>([]);
  const [contractorSelected, setContractorSelected] = useState<ContractorsResponse | null>(initialContractor ?? null);
  const [loadingData, setLoadingData] = useState(true);
  const [modalNewTablePrices, setModalNewTablePrices] = useState(false);

  const [services, setServices] = useState<ServicesResponse[]>([]);
  const [service, setService] = useState<ServicesResponse | null>(null);

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [category, setCategory] = useState<ServiceCategory | null>(null);

  const [tableId, setTableId] = useState<TableIdType | null>(null);
  const [tableType, setTableType] = useState<TableTypeOption | null>(null);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');

  const [pricingTemplates, setPricingTemplates] = useState<PricingTemplate[]>([]);
  const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);
  const [nameError, setNameError] = useState(false);

  const resetTableSelection = useCallback(() => {
    setTablePrices(undefined);
    setTableId(null);
    setNewTablePrices(undefined);
    setTableType(null);
  }, []);

  const showApiError = useCallback((err: any, fallback = '') => {
    const error = handleApiErrorResponse(err);
    const responseData = err?.response?.data;
    const responseMessage =
      responseData?.message || responseData?.error || responseData?.errorDescription || responseData?.data;

    setSeverity('warning');
    setMessageSnack(error?.handledMessage || error?.applicationErrorMessage || error?.apiError || responseMessage || fallback);
    setOpenSnackbar(true);
  }, []);

  const loadPricingTemplates = useCallback(async () => {
    setLoadingData(true);
    try {
      const { data } = await listPricingTemplatesApi();
      const templates = Array.isArray(data) ? data : data?.data ?? [];
      setPricingTemplates(templates);
      setHasLoadedTemplates(true);
    } catch (err) {
      setPricingTemplates([]);
      setHasLoadedTemplates(true);
      console.error('[loadPricingTemplates] GET /pricing-templates falhou:', err);
      showApiError(err, 'Erro ao carregar tabelas de serviço. Verifique sua conexão ou tente novamente mais tarde.');
    } finally {
      setLoadingData(false);
    }
  }, [showApiError]);

  const loadPricingTemplateById = useCallback(
    async (templateId: string) => {
      setLoadingData(true);
      try {
        const { data } = await getPricingTemplateByIdApi(templateId);
        const template = data?.data ?? data;

        setTablePrices(mapTemplateToTablePrice(template));
        setTableType(getTemplateTypeOption(template?.tipo_tabela));
      } catch (err) {
        setTablePrices(undefined);
        showApiError(err, 'Erro ao consultar tabela de serviço');
      } finally {
        setLoadingData(false);
      }
    },
    [showApiError],
  );

  const handleChangeService = useCallback(
    async (selectedService: ServicesResponse | null) => {
      if (!selectedService) {
        setService(null);
        setCategories([]);
        setCategory(null);
        return;
      }

      try {
        setLoadingData(true);
        const { data } = await loadCategoriesByServices(selectedService.id);
        setCategories(data || []);
        setService(selectedService);
        setCategory(null);
      } catch (err) {
        showApiError(err, 'Erro ao carregar categorias do serviço');
      } finally {
        setLoadingData(false);
      }
    },
    [showApiError],
  );

  const handleChangeCategory = useCallback((serviceCategory: ServiceCategory | null) => {
    setCategory(serviceCategory);
  }, []);

  useEffect(() => {
    if (initialContractor) {
      setContractorSelected(initialContractor);
    }
  }, [initialContractor]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [contractorsRes, servicesRes] = await Promise.all([loadContractors(), loadServices()]);
        setContractors((contractorsRes as { data: ContractorsResponse[] }).data ?? []);
        setServices((servicesRes as { data: ServicesResponse[] }).data ?? []);
      } catch (err) {
        showApiError(err, 'Erro ao carregar dados da tela');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
    loadPricingTemplates();
  }, [loadPricingTemplates, showApiError]);

  useEffect(() => {
    if (!tableId?.id) {
      setTablePrices(undefined);
      return;
    }

    loadPricingTemplateById(tableId.id);
  }, [loadPricingTemplateById, tableId?.id]);

  const handleSubmit = (table?: TablePrice) => async () => {
    if (!table) {
      return;
    }

    if (!table.name?.trim()) {
      setNameError(true);
      return;
    }
    setNameError(false);

    setLoading(true);
    try {
      const payload = {
        diaria_na_base: getPriceValue(table, 'DAILY'),
        equipamento: table.equipment?.name?.trim() || '',
        hp: getPriceValue(table, 'STOP_TIME'),
        ht: getPriceValue(table, 'HT'),
        km_excedente: getPriceValue(table, 'OVER_DISTANCE'),
        nome: table.name?.trim() || '',
        saida_ate_40km: getPriceValue(table, 'OUTPUT_LIMIT'),
        saida_noite_sabdom: getPriceValue(table, 'SPECIAL_EXIT'),
        taxa_cancelamento_padrao: getPriceValue(table, 'DEFAULT_CANCELLATION_FEE'),
        taxa_cancelamento_saida: getPriceValue(table, 'CANCELLATION_FEE'),
        tipo_tabela: tableType?.[1] ?? 'Padrão',
        valor_equipamento: Number(table.equipment?.value || 0),
      };

      await createPricingTemplateApi(payload);

      setSeverity('success');
      setMessageSnack('Tabela de serviço cadastrada com sucesso!');
      setOpenSnackbar(true);
      setModalNewTablePrices(false);
      setNewTablePrices(undefined);
      setTableType(null);
      setNameError(false);
      resetTableSelection();
      await loadPricingTemplates();
    } catch (err) {
      showApiError(err, 'Erro ao cadastrar tabela de serviço');
    } finally {
      setLoading(false);
    }
  };

  const priceTables: TableIdType[] = useMemo(
    () =>
      pricingTemplates.map(template => ({
        id: template.id,
        label: getTemplateLabel(template),
        type: getTemplateTypeOption(template.tipo_tabela)?.[0] ?? 'DEFAULT_PRICING',
      })),
    [pricingTemplates],
  );

  const handleChangePrice = (
    code: PricingAssociationType,
    value: number,
    stateParam: React.Dispatch<React.SetStateAction<TablePrice | undefined>> = setTablePrices,
  ) => {
    stateParam(prev => {
      const base = prev?.prices ?? [];
      const filtered = base.filter(item => item.code !== code);
      filtered.push({ code, value });
      return { ...prev, prices: filtered } as TablePrice;
    });
  };

  const handleChangeName = (
    name?: string,
    stateParam: React.Dispatch<React.SetStateAction<TablePrice | undefined>> = setNewTablePrices,
  ) => {
    stateParam(prev => ({ ...prev, name, prices: prev?.prices }));
  };

  const handleChangeEquipment = (
    field: 'name' | 'value',
    value: string | number,
    stateParam: React.Dispatch<React.SetStateAction<TablePrice | undefined>> = setNewTablePrices,
  ) => {
    stateParam(prev => {
      const current = prev?.equipment ?? { name: '', value: 0 };
      const next = field === 'name' ? { ...current, name: value as string } : { ...current, value: value as number };
      return { ...prev, equipment: next };
    });
  };

  const handleToggleModalNewTablePrices = () => {
    setModalNewTablePrices(prev => !prev);
    setNewTablePrices(undefined);
    setTableType(null);
    setNameError(false);
  };

  const handleSaveContractorPricing = async () => {
    if (!contractorSelected || !service || !category || !tablePrices) {
      setSeverity('warning');
      setMessageSnack('Selecione prestador, serviço, categoria e tabela antes de salvar.');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        categoria: category.description,
        diaria_na_base: getPriceValue(tablePrices, 'DAILY'),
        equipamento: tablePrices.equipment?.name?.trim() || '',
        hora_parada: getPriceValue(tablePrices, 'STOP_TIME'),
        hp: getPriceValue(tablePrices, 'STOP_TIME'),
        ht: getPriceValue(tablePrices, 'HT'),
        km_excedente: getPriceValue(tablePrices, 'OVER_DISTANCE'),
        nome: tablePrices.name?.trim() || '',
        prestador_id: contractorSelected.id,
        saida_ate_40km: getPriceValue(tablePrices, 'OUTPUT_LIMIT'),
        saida_noite_sabdom: getPriceValue(tablePrices, 'SPECIAL_EXIT'),
        servico: service.description,
        taxa_cancelamento: getPriceValue(tablePrices, 'CANCELLATION_FEE'),
        tipo_tabela: tableType?.[1] ?? 'Padrão',
        valor_equipamento: Number(tablePrices.equipment?.value || 0),
      };

      await createContractorPricingV1Api(payload);

      setSeverity('success');
      setMessageSnack('Tabela de preços salva para o prestador com sucesso!');
      setOpenSnackbar(true);
    } catch (err) {
      showApiError(err, 'Erro ao salvar tabela de preços do prestador');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!embedded && <Title>Tabela de preços do prestador (backoffice)</Title>}

      <Grid container spacing={2} alignItems="flex-end">
        {!initialContractor && (
          <Grid item xs={12} md={6} lg={4}>
            <CustomAutocomplete
              id="combo-box-contractors"
              options={contractors}
              getOptionLabel={item => (item.name || item.organization || item.id).replace(/\s+/g, ' ').trim()}
              renderInput={params => <TextField {...params} label="Selecione o prestador" />}
              onChange={(_, value) => setContractorSelected(value)}
              value={contractorSelected ?? null}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6} lg={4}>
          <CustomAutocomplete
            id="combo-box-services-contractor"
            options={services}
            getOptionLabel={item => item.description}
            renderInput={params => <TextField {...params} label="Selecione o serviço" />}
            onChange={(_, value) => handleChangeService(value)}
            value={service ?? null}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <CustomAutocomplete
            id="combo-box-categories-contractor"
            options={categories}
            getOptionLabel={item => item.description}
            renderInput={params => <TextField {...params} label="Selecione a categoria" />}
            onChange={(_, value) => handleChangeCategory(value)}
            value={category ?? null}
          />
        </Grid>
        <Grid item xs={12} />

        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            id="combo-box-table-contractor"
            options={priceTables}
            getOptionLabel={item => item.label ?? ''}
            renderInput={params => <TextField {...params} label="Selecione a tabela cadastrada" />}
            onChange={(_, value) => setTableId(value)}
            value={tableId}
            width={260}
            disabled={priceTables.length === 0}
          />
        </Grid>

        <Grid item container xs={12} md={6} justify="flex-end" style={{ display: 'flex' }}>
          <Tooltip title="Nova tabela individual" arrow>
            <span>
              <ButtonMUI onClick={handleToggleModalNewTablePrices}>
                <Add />
              </ButtonMUI>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <Form>
        {!(contractorSelected || initialContractor) || !service || !category ? (
          <Grid container>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                value="Selecione o prestador, serviço e categoria para visualizar ou editar a tabela."
                InputProps={{ readOnly: true }}
                style={{ color: '#888' }}
              />
            </Grid>
          </Grid>
        ) : (
          <>
        {hasLoadedTemplates && priceTables.length === 0 && (
          <Grid container>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                value="Nenhuma tabela de serviço cadastrada no sistema."
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        )}

        <Grid container xs={12}>
          <Grid container item spacing={2} sm={12} md={6}>
            <Grid item xs={6}>
              <Subtitle>Serviço</Subtitle>
            </Grid>
            <Grid item xs={6}>
              <Subtitle>Valor</Subtitle>
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Nome da tabela</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                value={tablePrices?.name ?? ''}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { cursor: 'not-allowed', backgroundColor: '#f5f5f5' } }}
                style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Saída (Até 40 Km)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'OUTPUT_LIMIT'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Km Excedente</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'OVER_DISTANCE'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Hora parada - HP</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'STOP_TIME'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Hora trabalhada - HT</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'HT'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Diária na Base</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'DAILY'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Taxa de Cancelamento (Saída)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'CANCELLATION_FEE'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Taxa de Cancelamento (Padrão)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                value={formatPrice(getPriceValue(tablePrices, 'DEFAULT_CANCELLATION_FEE'), false)}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Saída (noite, sab/dom)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(getPriceValue(tablePrices, 'SPECIAL_EXIT'), false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Equipamento</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" fullWidth variant="outlined" value={tablePrices?.equipment?.name ?? ''} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Valor do equipamento</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent value={formatPrice(tablePrices?.equipment?.value || 0, false)} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12}>
              <Button
                label="Salvar"
                type="button"
                loading={loading}
                filled
                style={{ width: 200 }}
                onClick={handleSaveContractorPricing}
              />
            </Grid>
          </Grid>
        </Grid>
          </>
        )}
      </Form>

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
      <Modal open={loadingData}>
        <Loading />
      </Modal>

      <Modal
        open={modalNewTablePrices}
        buttons={[
          <Button key="cancel" type="button" filled label="Cancelar" onClick={handleToggleModalNewTablePrices} />,
          <Button key="save" type="button" filled label="Salvar" onClick={handleSubmit(newTablePrices)} loading={loading} />,
        ]}
        fullWidth
      >
        <Grid container item xs={12}>
          <Grid container item spacing={2} sm={12} md={6}>
            <Grid item xs={6}>
              <Subtitle>Serviço</Subtitle>
            </Grid>
            <Grid item xs={6}>
              <Subtitle>Valor</Subtitle>
            </Grid>

            <Grid item xs={6}>
              <FormLabel error={nameError}>Nome da tabela *</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                value={newTablePrices?.name ?? ''}
                onChange={e => {
                  if (nameError && e.target.value.trim()) setNameError(false);
                  handleChangeName(e.target.value, setNewTablePrices);
                }}
                inputProps={{ maxLength: 100 }}
                error={nameError}
                helperText={nameError ? 'Nome da tabela é obrigatório' : undefined}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Saída (Até 40 Km)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('OUTPUT_LIMIT', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'OUTPUT_LIMIT'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Km Excedente</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('OVER_DISTANCE', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'OVER_DISTANCE'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Hora parada - HP</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('STOP_TIME', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'STOP_TIME'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Hora trabalhada - HT</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('HT', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'HT'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Diária na Base</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('DAILY', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'DAILY'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Taxa de Cancelamento (Saída)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('CANCELLATION_FEE', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'CANCELLATION_FEE'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Taxa de Cancelamento (Padrão)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e =>
                  handleChangePrice('DEFAULT_CANCELLATION_FEE', maskAndFormatPrice(e.target.value), setNewTablePrices)
                }
                value={formatPrice(getPriceValue(newTablePrices, 'DEFAULT_CANCELLATION_FEE'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Saída (noite, sab/dom)</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangePrice('SPECIAL_EXIT', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(getPriceValue(newTablePrices, 'SPECIAL_EXIT'), false)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Equipamento</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Ex: Guincho pesado"
                value={newTablePrices?.equipment?.name ?? ''}
                onChange={e => handleChangeEquipment('name', e.target.value, setNewTablePrices)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormLabel>Valor do equipamento</FormLabel>
            </Grid>
            <Grid item xs={6}>
              <InputComponent
                onChange={e => handleChangeEquipment('value', maskAndFormatPrice(e.target.value), setNewTablePrices)}
                value={formatPrice(newTablePrices?.equipment?.value || 0, false)}
              />
            </Grid>
          </Grid>

          <Grid container item sm={12} md={6}>
            <Grid item sm={12}>
              <CustomAutocomplete
                id="combo-box-table-types-contractor"
                options={TABLE_TYPE_OPTIONS}
                getOptionLabel={item => item[1] ?? ''}
                renderInput={params => <TextField {...params} label="Selecione o tipo da tabela" />}
                onChange={(_, value) => setTableType(value)}
                value={tableType}
              />
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}
